import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Feature, FeatureCategory, FeaturesByCategory, Task, TaskStatus, AITeamMember, FeaturePriority, FeatureStatus } from '../types';
import {
  getFeaturesByCategory,
  getFeaturesByCategoryWithStatus,
  getFeature,
  createFeature,
  updateFeature,
  deleteFeature,
  createCategory,
  updateCategory,
  deleteCategory,
  reorderCategories,
  reorderFeatures,
  moveFeature,
  getGeneralPrompt,
  updateGeneralPrompt,
  getTasksByFeature,
  getTeamMembers,
  getNameSuggestions,
  getAllNames,
  markFeatureDone,
  reopenFeature,
} from '../services/api';
import { useTheme } from '../context/ThemeContext';
import TaskDetail from '../components/TaskDetail';

// Priority display labels and colors
const PRIORITY_LABELS: { [key: string]: string } = {
  core: 'Core',
  // 2026
  'april-2026': 'April 2026',
  'may-2026': 'May 2026',
  'june-2026': 'June 2026',
  'july-2026': 'July 2026',
  'august-2026': 'August 2026',
  'september-2026': 'September 2026',
  'october-2026': 'October 2026',
  'november-2026': 'November 2026',
  'december-2026': 'December 2026',
  // 2027
  'january-2027': 'January 2027',
  'february-2027': 'February 2027',
  'march-2027': 'March 2027',
  'april-2027': 'April 2027',
  'may-2027': 'May 2027',
  'june-2027': 'June 2027',
  'july-2027': 'July 2027',
  'august-2027': 'August 2027',
  'september-2027': 'September 2027',
  'october-2027': 'October 2027',
  'november-2027': 'November 2027',
  'december-2027': 'December 2027',
  unscheduled: 'Unscheduled',
  deprioritized: 'Deprioritized',
};

const PRIORITY_COLORS: { [key: string]: string } = {
  core: '#dc2626',  // Red - critical
  // 2026 - gradient from orange to cyan
  'april-2026': '#ea580c',  // Orange
  'may-2026': '#f59e0b',  // Amber
  'june-2026': '#ca8a04',  // Yellow
  'july-2026': '#84cc16',  // Lime
  'august-2026': '#16a34a',  // Green
  'september-2026': '#059669',  // Emerald
  'october-2026': '#0891b2',  // Cyan
  'november-2026': '#0284c7',  // Sky
  'december-2026': '#2563eb',  // Blue
  // 2027 - gradient from indigo to pink
  'january-2027': '#4f46e5',  // Indigo
  'february-2027': '#7c3aed',  // Violet
  'march-2027': '#9333ea',  // Purple
  'april-2027': '#c026d3',  // Fuchsia
  'may-2027': '#db2777',  // Pink
  'june-2027': '#e11d48',  // Rose
  'july-2027': '#dc2626',  // Red
  'august-2027': '#ea580c',  // Orange
  'september-2027': '#f59e0b',  // Amber
  'october-2027': '#ca8a04',  // Yellow
  'november-2027': '#84cc16',  // Lime
  'december-2027': '#16a34a',  // Green
  unscheduled: '#6b7280',  // Gray
  deprioritized: '#9ca3af',  // Light gray
};

// Status display labels and colors
const STATUS_LABELS: { [key: string]: string } = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  in_review: 'In Review',
  done: 'Done',
};

const STATUS_COLORS: { [key: string]: string } = {
  not_started: '#6b7280',  // Gray
  in_progress: '#3b82f6',  // Blue
  in_review: '#8b5cf6',  // Purple
  done: '#10b981',  // Green
};

// Owner Input with Autocomplete component
const OwnerInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, onChange, placeholder = "Enter owner name", disabled = false }) => {
  const { theme } = useTheme();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (newValue.length > 0) {
      try {
        const nameSuggestions = await getNameSuggestions(newValue);
        setSuggestions(nameSuggestions);
        setShowSuggestions(nameSuggestions.length > 0);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelect = (name: string) => {
    onChange(name);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => value && suggestions.length > 0 && setShowSuggestions(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '12px',
          border: `1px solid ${theme.border}`,
          borderRadius: '6px',
          fontSize: '14px',
          backgroundColor: disabled ? theme.surfaceHover : theme.surface,
          color: theme.text,
        }}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: '6px',
          marginTop: '4px',
          maxHeight: '200px',
          overflowY: 'auto',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
        }}>
          {suggestions.map((name, index) => (
            <div
              key={index}
              onClick={() => handleSelect(name)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderBottom: index < suggestions.length - 1 ? `1px solid ${theme.border}` : 'none',
                color: theme.text,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.surfaceHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Collapsible section component for long text
const CollapsibleSection: React.FC<{
  title: string;
  content: string;
  onChange: (value: string) => void;
  isEditing: boolean;
}> = ({ title, content, onChange, isEditing }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { theme } = useTheme();
  const hasContent = content && content.trim().length > 0;
  const previewLength = 150;

  return (
    <div style={{ marginBottom: '16px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <label style={{ fontWeight: '500', color: theme.text }}>{title}</label>
        {hasContent && !isEditing && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              backgroundColor: theme.surfaceHover,
              border: `1px solid ${theme.border}`,
              borderRadius: '4px',
              cursor: 'pointer',
              color: theme.textSecondary,
            }}
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
        )}
      </div>
      {isEditing ? (
        <textarea
          value={content}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%',
            minHeight: '150px',
            padding: '12px',
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            fontFamily: 'monospace',
            fontSize: '13px',
            backgroundColor: theme.surface,
            color: theme.text,
            resize: 'vertical',
          }}
        />
      ) : (
        <div
          onClick={() => {
            if (hasContent && !isExpanded) {
              setIsExpanded(true);
            }
          }}
          style={{
            padding: '12px',
            backgroundColor: theme.surfaceHover,
            borderRadius: '6px',
            fontSize: '13px',
            whiteSpace: 'pre-wrap',
            color: theme.text,
            maxHeight: isExpanded ? 'none' : '100px',
            overflow: isExpanded ? 'visible' : 'hidden',
            position: 'relative',
            cursor: hasContent && !isExpanded ? 'pointer' : 'default',
          }}
          title={hasContent && !isExpanded ? 'Click to expand' : undefined}
        >
          {hasContent ? (
            isExpanded ? content : content.substring(0, previewLength) + (content.length > previewLength ? '...' : '')
          ) : (
            <span style={{ color: theme.textSecondary, fontStyle: 'italic' }}>No content</span>
          )}
        </div>
      )}
    </div>
  );
};

// Task status badge colors
const getStatusColor = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.QUEUED: return '#f59e0b';
    case TaskStatus.IN_PROGRESS: return '#3b82f6';
    case TaskStatus.READY_FOR_REVIEW: return '#8b5cf6';
    case TaskStatus.COMPLETED: return '#10b981';
    case TaskStatus.FAILED: return '#ef4444';
    default: return '#6b7280';
  }
};

// Feature Detail Modal
const FeatureDetailModal: React.FC<{
  feature: Feature | null;
  onClose: () => void;
  onSave: (updates: Partial<Feature>) => void;
  onDelete: () => void;
  onMarkDone?: (featureId: string) => void;
  onReopen?: (featureId: string) => void;
}> = ({ feature, onClose, onSave, onDelete, onMarkDone, onReopen }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editedFeature, setEditedFeature] = useState<Partial<Feature>>({});
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showTasks, setShowTasks] = useState(true);
  const [teamMembers, setTeamMembers] = useState<AITeamMember[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  useEffect(() => {
    if (feature) {
      setEditedFeature({
        name: feature.name,
        expectations: feature.expectations,
        changes: feature.changes,
        inputs: feature.inputs,
        prompt: feature.prompt,
        owner: feature.owner || '',
        priority: feature.priority || 'unscheduled',
      });
      // Load tasks and team members for this feature
      loadFeatureTasks(feature.id);
      loadTeamMembers();
    }
  }, [feature]);

  const loadFeatureTasks = async (featureId: string) => {
    setLoadingTasks(true);
    try {
      const featureTasks = await getTasksByFeature(featureId);
      setTasks(featureTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
    setLoadingTasks(false);
  };

  const loadTeamMembers = async () => {
    try {
      const members = await getTeamMembers();
      setTeamMembers(members);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const getTeamMemberRole = (name: string): string => {
    const member = teamMembers.find(m => m.name === name);
    return member ? member.role : name;
  };

  if (!feature) return null;

  const handleSave = () => {
    onSave(editedFeature);
    setIsEditing(false);
  };

  const handleCreateTask = () => {
    navigate(`/create?feature=${encodeURIComponent(feature.id)}`, {
      state: { returnToFeatureId: feature.id },
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: theme.surface,
          borderRadius: '12px',
          width: '90%',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '24px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Mobile responsive with sticky action buttons */}
        <div style={{ marginBottom: '24px' }}>
          {/* Action buttons - always visible at top */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginBottom: '12px',
            flexWrap: 'wrap',
          }}>
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditedFeature({
                      name: feature.name,
                      expectations: feature.expectations,
                      changes: feature.changes,
                      inputs: feature.inputs,
                      prompt: feature.prompt,
                      owner: feature.owner || '',
                    });
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: theme.surfaceHover,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={onDelete}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#ef4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                backgroundColor: theme.surfaceHover,
                color: theme.text,
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
          {/* Title - below buttons */}
          {isEditing ? (
            <input
              type="text"
              value={editedFeature.name || ''}
              onChange={(e) => setEditedFeature({ ...editedFeature, name: e.target.value })}
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                padding: '8px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                width: '100%',
                backgroundColor: theme.surface,
                color: theme.text,
                boxSizing: 'border-box',
              }}
              placeholder="Feature name"
            />
          ) : (
            <h2 style={{ margin: 0, color: theme.text, wordBreak: 'break-word' }}>{feature.name}</h2>
          )}
        </div>

        {/* Owner Field */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.text }}>
            Owner
          </label>
          {isEditing ? (
            <OwnerInput
              value={editedFeature.owner || ''}
              onChange={(value) => setEditedFeature({ ...editedFeature, owner: value })}
              placeholder="Assign an owner..."
            />
          ) : (
            <div
              style={{
                padding: '12px',
                backgroundColor: theme.surfaceHover,
                borderRadius: '6px',
                fontSize: '14px',
                color: feature.owner ? theme.text : theme.textSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {feature.owner ? (
                <>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}>
                    {feature.owner.charAt(0).toUpperCase()}
                  </span>
                  <span>{feature.owner}</span>
                </>
              ) : (
                <span style={{ fontStyle: 'italic' }}>No owner assigned</span>
              )}
            </div>
          )}
        </div>

        {/* Priority and Status Fields */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {/* Priority Field */}
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.text }}>
              Priority
            </label>
            {isEditing ? (
              <select
                value={editedFeature.priority || 'unscheduled'}
                onChange={(e) => setEditedFeature({ ...editedFeature, priority: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: theme.surface,
                  color: theme.text,
                }}
              >
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            ) : (
              <div
                style={{
                  padding: '12px',
                  backgroundColor: theme.surfaceHover,
                  borderRadius: '6px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: `${PRIORITY_COLORS[feature.priority || 'unscheduled']}20`,
                    color: PRIORITY_COLORS[feature.priority || 'unscheduled'],
                  }}
                >
                  {PRIORITY_LABELS[feature.priority || 'unscheduled']}
                </span>
              </div>
            )}
          </div>

          {/* Status Field */}
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.text }}>
              Status
            </label>
            <div
              style={{
                padding: '12px',
                backgroundColor: theme.surfaceHover,
                borderRadius: '6px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: `${STATUS_COLORS[feature.computed_status || 'not_started']}20`,
                  color: STATUS_COLORS[feature.computed_status || 'not_started'],
                }}
              >
                {STATUS_LABELS[feature.computed_status || 'not_started']}
              </span>
              {/* Mark as Done / Reopen buttons */}
              {feature.computed_status !== 'done' && onMarkDone && (
                <button
                  onClick={() => onMarkDone(feature.id)}
                  style={{
                    padding: '4px 10px',
                    backgroundColor: '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                  }}
                >
                  Mark as Done
                </button>
              )}
              {feature.computed_status === 'done' && onReopen && (
                <button
                  onClick={() => onReopen(feature.id)}
                  style={{
                    padding: '4px 10px',
                    backgroundColor: theme.surfaceHover,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                  }}
                >
                  Reopen
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Expectations */}
        <CollapsibleSection
          title="Expectations"
          content={isEditing ? (editedFeature.expectations || '') : (feature.expectations || '')}
          onChange={(value) => setEditedFeature({ ...editedFeature, expectations: value })}
          isEditing={isEditing}
        />

        {/* Feature-Specific Prompt */}
        <CollapsibleSection
          title="AI Instructions (Feature-Specific Prompt)"
          content={isEditing ? (editedFeature.prompt || '') : (feature.prompt || '')}
          onChange={(value) => setEditedFeature({ ...editedFeature, prompt: value })}
          isEditing={isEditing}
        />

        {/* Previous Changes */}
        <CollapsibleSection
          title="Previous Changes (AI-Updated)"
          content={isEditing ? (editedFeature.changes || '') : (feature.changes || '')}
          onChange={(value) => setEditedFeature({ ...editedFeature, changes: value })}
          isEditing={isEditing}
        />

        {/* Human Inputs */}
        <CollapsibleSection
          title="Previous Human Inputs"
          content={isEditing ? (editedFeature.inputs || '') : (feature.inputs || '')}
          onChange={(value) => setEditedFeature({ ...editedFeature, inputs: value })}
          isEditing={isEditing}
        />

        {/* Tasks Section */}
        <div style={{ marginTop: '24px', marginBottom: '16px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ fontWeight: '500', color: theme.text }}>
                Tasks ({tasks.length})
              </label>
              <button
                onClick={() => setShowTasks(!showTasks)}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  backgroundColor: theme.surfaceHover,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  color: theme.textSecondary,
                }}
              >
                {showTasks ? 'Hide' : 'Show'}
              </button>
            </div>
            <button
              onClick={handleCreateTask}
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                backgroundColor: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              + New Task
            </button>
          </div>

          {showTasks && (
            <div
              style={{
                backgroundColor: theme.surfaceHover,
                borderRadius: '6px',
                padding: loadingTasks || tasks.length === 0 ? '16px' : '8px',
                maxHeight: '300px',
                overflowY: 'auto',
              }}
            >
              {loadingTasks ? (
                <div style={{ textAlign: 'center', color: theme.textSecondary }}>
                  Loading tasks...
                </div>
              ) : tasks.length === 0 ? (
                <div style={{ textAlign: 'center', color: theme.textSecondary }}>
                  No tasks for this feature yet. Click "+ New Task" to create one.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      onClick={() => setSelectedTask(task)}
                      style={{
                        backgroundColor: theme.surface,
                        padding: '12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        border: `1px solid ${theme.border}`,
                        transition: 'border-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#3b82f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = theme.border;
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                        <span style={{ fontWeight: '500', color: theme.text, flex: 1 }}>
                          {task.title}
                        </span>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '500',
                            backgroundColor: getStatusColor(task.status),
                            color: '#fff',
                            marginLeft: '8px',
                          }}
                        >
                          {task.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                        {task.created_by} • {getTeamMemberRole(task.ai_team_member)} • {new Date(task.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Metadata */}
        <div style={{ marginTop: '24px', padding: '12px', backgroundColor: theme.surfaceHover, borderRadius: '6px' }}>
          <p style={{ margin: 0, fontSize: '12px', color: theme.textSecondary }}>
            Category: <strong>{feature.category}</strong> |
            Created: {new Date(feature.created_at).toLocaleDateString()} |
            Updated: {new Date(feature.updated_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Task Detail Modal - opens as overlay without navigating away */}
      {selectedTask && (
        <TaskDetail
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onTaskUpdate={() => {
            // Refresh the tasks list when task is updated
            if (feature) {
              loadFeatureTasks(feature.id);
            }
          }}
        />
      )}
    </div>
  );
};

// Create Feature Modal
const CreateFeatureModal: React.FC<{
  categories: FeatureCategory[];
  onClose: () => void;
  onCreate: (name: string, category: string, expectations: string, owner: string, priority?: string) => void;
  initialPriority?: string;
}> = ({ categories, onClose, onCreate, initialPriority }) => {
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [category, setCategory] = useState(categories[0]?.id || '');
  const [expectations, setExpectations] = useState('');
  const [owner, setOwner] = useState('');
  const [priority, setPriority] = useState(initialPriority || 'unscheduled');

  const handleCreate = () => {
    if (!name.trim()) {
      alert('Please enter a feature name');
      return;
    }
    onCreate(name, category, expectations, owner, priority);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: theme.surface,
          borderRadius: '12px',
          width: '90%',
          maxWidth: '500px',
          padding: '24px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 24px 0', color: theme.text }}>Create New Feature</h2>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.text }}>
            Feature Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Quest Creation Flow"
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: theme.surface,
              color: theme.text,
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.text }}>
            Category *
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: theme.surface,
              color: theme.text,
            }}
          >
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.text }}>
            Expectations
          </label>
          <textarea
            value={expectations}
            onChange={(e) => setExpectations(e.target.value)}
            placeholder="What should this feature accomplish? What does success look like?"
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              fontSize: '14px',
              resize: 'vertical',
              backgroundColor: theme.surface,
              color: theme.text,
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.text }}>
            Owner
          </label>
          <OwnerInput
            value={owner}
            onChange={setOwner}
            placeholder="Assign an owner (optional)"
          />
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: theme.textSecondary }}>
            The team member responsible for this feature
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.text }}>
            Priority
          </label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: theme.surface,
              color: theme.text,
            }}
          >
            {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: theme.textSecondary }}>
            When this feature should be completed
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: theme.surfaceHover,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Create Feature
          </button>
        </div>
      </div>
    </div>
  );
};

// Create Category Modal
const CreateCategoryModal: React.FC<{
  onClose: () => void;
  onCreate: (id: string, name: string) => void;
}> = ({ onClose, onCreate }) => {
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [id, setId] = useState('');

  const handleCreate = () => {
    if (!name.trim() || !id.trim()) {
      alert('Please enter both ID and name');
      return;
    }
    onCreate(id, name);
    onClose();
  };

  // Auto-generate ID from name
  const handleNameChange = (value: string) => {
    setName(value);
    if (!id || id === name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')) {
      setId(value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, ''));
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: theme.surface,
          borderRadius: '12px',
          width: '90%',
          maxWidth: '400px',
          padding: '24px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 24px 0', color: theme.text }}>Create New Category</h2>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.text }}>
            Category Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g., Data Visualization"
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: theme.surface,
              color: theme.text,
            }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: theme.text }}>
            Category ID *
          </label>
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="e.g., data-visualization"
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'monospace',
              backgroundColor: theme.surface,
              color: theme.text,
            }}
          />
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: theme.textSecondary }}>
            Used internally. Lowercase letters, numbers, and hyphens only.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: theme.surfaceHover,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Create Category
          </button>
        </div>
      </div>
    </div>
  );
};

// General Prompt Modal
const GeneralPromptModal: React.FC<{
  prompt: string;
  onClose: () => void;
  onSave: (prompt: string) => void;
}> = ({ prompt, onClose, onSave }) => {
  const { theme } = useTheme();
  const [editedPrompt, setEditedPrompt] = useState(prompt);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: theme.surface,
          borderRadius: '12px',
          width: '90%',
          maxWidth: '900px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '24px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 16px 0', color: theme.text }}>General AI Instructions</h2>
        <p style={{ margin: '0 0 16px 0', color: theme.textSecondary, fontSize: '14px' }}>
          This prompt is included with ALL AI requests. It provides authoritative context that overrides any conflicting information.
        </p>

        <textarea
          value={editedPrompt}
          onChange={(e) => setEditedPrompt(e.target.value)}
          style={{
            flex: 1,
            minHeight: '400px',
            padding: '12px',
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            fontFamily: 'monospace',
            fontSize: '13px',
            resize: 'none',
            marginBottom: '16px',
            backgroundColor: theme.surface,
            color: theme.text,
          }}
        />

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: theme.surfaceHover,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onSave(editedPrompt);
              onClose();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Features Page
const Features: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const [data, setData] = useState<FeaturesByCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [showCreateFeature, setShowCreateFeature] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [showGeneralPrompt, setShowGeneralPrompt] = useState(false);
  const [generalPrompt, setGeneralPrompt] = useState('');
  const [draggedFeature, setDraggedFeature] = useState<Feature | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<string | null>(null);
  const [ownerFilter, setOwnerFilter] = useState<string>('');
  const [allOwners, setAllOwners] = useState<string[]>([]);
  // Default to current month-year for priority filter
  const getCurrentMonthPriority = (): string => {
    const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
                        'july', 'august', 'september', 'october', 'november', 'december'];
    const now = new Date();
    const currentMonth = monthNames[now.getMonth()];
    const currentYear = now.getFullYear();
    const monthYear = `${currentMonth}-${currentYear}`;
    // Check if current month-year is in PRIORITY_LABELS, otherwise return empty
    return PRIORITY_LABELS[monthYear] ? monthYear : '';
  };
  const [priorityFilter, setPriorityFilter] = useState<string>(getCurrentMonthPriority());
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityLevels, setPriorityLevels] = useState<string[]>(Object.keys(PRIORITY_LABELS));
  const [statusValues, setStatusValues] = useState<string[]>(Object.keys(STATUS_LABELS));

  const loadData = useCallback(async () => {
    try {
      const [featuresData, promptData] = await Promise.all([
        getFeaturesByCategoryWithStatus(priorityFilter || undefined, statusFilter || undefined),
        getGeneralPrompt(),
      ]);
      setData(featuresData);
      setGeneralPrompt(promptData);

      // Update priority levels and status values from API response
      if (featuresData.priority_levels) {
        setPriorityLevels(featuresData.priority_levels);
      }
      if (featuresData.status_values) {
        setStatusValues(featuresData.status_values);
      }

      // Extract unique owners from all features
      const owners = new Set<string>();
      Object.values(featuresData.features).forEach(featureList => {
        featureList.forEach((feature: Feature) => {
          if (feature.owner) {
            owners.add(feature.owner);
          }
        });
      });
      setAllOwners(Array.from(owners).sort());

      setLoading(false);
    } catch (error) {
      console.error('Error loading features:', error);
      setLoading(false);
    }
  }, [priorityFilter, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateFeature = async (name: string, category: string, expectations: string, owner: string, priority?: string) => {
    try {
      await createFeature(name, category, expectations, undefined, owner, priority);
      loadData();
    } catch (error) {
      console.error('Error creating feature:', error);
      alert('Failed to create feature');
    }
  };

  const handleCreateCategory = async (id: string, name: string) => {
    try {
      await createCategory(id, name);
      loadData();
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Failed to create category');
    }
  };

  const handleFeatureClick = async (featureId: string) => {
    try {
      const feature = await getFeature(featureId);
      setSelectedFeature(feature);
    } catch (error) {
      console.error('Error loading feature:', error);
    }
  };

  // Re-open feature modal when returning from task creation
  useEffect(() => {
    const openFeatureId = (location.state as any)?.openFeatureId;
    if (openFeatureId) {
      handleFeatureClick(openFeatureId);
      window.history.replaceState({}, '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSaveFeature = async (updates: Partial<Feature>) => {
    if (!selectedFeature) return;
    try {
      await updateFeature(selectedFeature.id, updates);
      loadData();
      // Reload the feature to show updated data
      const updated = await getFeature(selectedFeature.id);
      setSelectedFeature(updated);
    } catch (error) {
      console.error('Error saving feature:', error);
      alert('Failed to save feature');
    }
  };

  const handleDeleteFeature = async () => {
    if (!selectedFeature) return;
    if (!window.confirm(`Are you sure you want to delete "${selectedFeature.name}"?`)) return;
    try {
      await deleteFeature(selectedFeature.id);
      setSelectedFeature(null);
      loadData();
    } catch (error) {
      console.error('Error deleting feature:', error);
      alert('Failed to delete feature');
    }
  };

  const handleMarkDone = async (featureId: string) => {
    try {
      await markFeatureDone(featureId);
      loadData();
      // Reload the selected feature to show updated status
      if (selectedFeature && selectedFeature.id === featureId) {
        const updated = await getFeature(featureId);
        // Add computed_status since getFeature doesn't return it
        setSelectedFeature({ ...updated, computed_status: 'done' });
      }
    } catch (error) {
      console.error('Error marking feature as done:', error);
      alert('Failed to mark feature as done');
    }
  };

  const handleReopen = async (featureId: string) => {
    try {
      await reopenFeature(featureId);
      loadData();
      // Reload the selected feature to show updated status
      if (selectedFeature && selectedFeature.id === featureId) {
        const updated = await getFeature(featureId);
        // Computed status will be recalculated on next data load
        setSelectedFeature({ ...updated, computed_status: 'in_progress' });
      }
    } catch (error) {
      console.error('Error reopening feature:', error);
      alert('Failed to reopen feature');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm(`Delete this category? Features will be moved to "Other".`)) return;
    try {
      await deleteCategory(categoryId);
      loadData();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  const handleSaveGeneralPrompt = async (prompt: string) => {
    try {
      await updateGeneralPrompt(prompt);
      setGeneralPrompt(prompt);
    } catch (error) {
      console.error('Error saving general prompt:', error);
      alert('Failed to save general prompt');
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent, feature: Feature) => {
    setDraggedFeature(feature);
    // Firefox requires dataTransfer.setData to be called for drag to work
    e.dataTransfer.setData('text/plain', feature.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, categoryId: string) => {
    e.preventDefault();
    setDragOverCategory(categoryId);
  };

  const handleDragLeave = () => {
    setDragOverCategory(null);
  };

  const handleDrop = async (e: React.DragEvent, targetCategory: string) => {
    e.preventDefault();
    setDragOverCategory(null);

    if (!draggedFeature) return;

    if (draggedFeature.category !== targetCategory) {
      try {
        await moveFeature(draggedFeature.id, targetCategory);
        loadData();
      } catch (error) {
        console.error('Error moving feature:', error);
        alert('Failed to move feature');
      }
    }

    setDraggedFeature(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: theme.background }}>
        <div style={{ color: theme.text }}>Loading features...</div>
      </div>
    );
  }

  const categories = data?.categories || [];
  const featuresByCategory = data?.features || {};

  return (
    <div style={{ backgroundColor: theme.background, minHeight: '100vh' }}>
      {/* Action Bar - NOT fixed, scrolls with content for better mobile experience */}
      <div
        className="features-action-bar"
        style={{
          backgroundColor: theme.surface,
          borderBottom: `1px solid ${theme.border}`,
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          maxWidth: '100%',
          overflowX: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: '18px', color: theme.text, whiteSpace: 'nowrap' }}>Features</h1>
          {/* Priority Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <label style={{ fontSize: '13px', color: theme.textSecondary, whiteSpace: 'nowrap' }}>Priority:</label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              style={{
                padding: '6px 10px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: theme.surface,
                color: theme.text,
                minWidth: 0,
                maxWidth: '140px',
              }}
            >
              <option value="">All</option>
              {priorityLevels.map((priority) => (
                <option key={priority} value={priority}>{PRIORITY_LABELS[priority] || priority}</option>
              ))}
            </select>
          </div>
          {/* Status Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <label style={{ fontSize: '13px', color: theme.textSecondary, whiteSpace: 'nowrap' }}>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '6px 10px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: theme.surface,
                color: theme.text,
                minWidth: 0,
                maxWidth: '140px',
              }}
            >
              <option value="">All</option>
              {statusValues.map((status) => (
                <option key={status} value={status}>{STATUS_LABELS[status] || status}</option>
              ))}
            </select>
          </div>
          {/* Owner Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <label style={{ fontSize: '13px', color: theme.textSecondary, whiteSpace: 'nowrap' }}>Owner:</label>
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              style={{
                padding: '6px 10px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: theme.surface,
                color: theme.text,
                minWidth: 0,
                maxWidth: '160px',
              }}
            >
              <option value="">All Owners</option>
              <option value="__unassigned__">Unassigned</option>
              {allOwners.map((owner) => (
                <option key={owner} value={owner}>{owner}</option>
              ))}
            </select>
          </div>
          {/* Clear All Filters */}
          {(priorityFilter || statusFilter || ownerFilter) && (
            <button
              onClick={() => {
                setPriorityFilter('');
                setStatusFilter('');
                setOwnerFilter('');
              }}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                border: `1px solid ${theme.border}`,
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                color: theme.textSecondary,
              }}
              title="Clear all filters"
            >
              Clear Filters
            </button>
          )}
        </div>
        <div className="feature-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowGeneralPrompt(true)}
            style={{
              padding: '8px 12px',
              backgroundColor: '#8b5cf6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            General Prompt
          </button>
          <button
            onClick={() => setShowCreateCategory(true)}
            style={{
              padding: '8px 12px',
              backgroundColor: '#f59e0b',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            + Category
          </button>
          <button
            onClick={() => setShowCreateFeature(true)}
            style={{
              padding: '8px 12px',
              backgroundColor: '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            + Feature
          </button>
        </div>
      </div>

      {/* Categories Grid */}
      <div style={{ padding: '16px', maxWidth: '100%', overflowX: 'hidden' }}>
        <div
          className="categories-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
            gap: '20px',
          }}
        >
          {categories.map((category) => (
            <div
              key={category.id}
              onDragOver={(e) => handleDragOver(e, category.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, category.id)}
              style={{
                backgroundColor: theme.surface,
                borderRadius: '12px',
                padding: '16px',
                border: dragOverCategory === category.id ? '2px dashed #3b82f6' : `1px solid ${theme.border}`,
                minHeight: '200px',
                transition: 'border 0.2s',
              }}
            >
              {/* Category Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, color: theme.text, fontSize: '16px' }}>{category.name}</h3>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <span
                    style={{
                      backgroundColor: theme.surfaceHover,
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      color: theme.textSecondary,
                    }}
                  >
                    {ownerFilter
                      ? `${(featuresByCategory[category.id] || []).filter((f) => {
                          if (ownerFilter === '__unassigned__') return !f.owner;
                          return f.owner === ownerFilter;
                        }).length}/${(featuresByCategory[category.id] || []).length}`
                      : (featuresByCategory[category.id] || []).length}
                  </span>
                  {category.id !== 'other' && (
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: theme.textSecondary,
                        fontSize: '14px',
                        padding: '0 4px',
                      }}
                      title="Delete category"
                    >
                      x
                    </button>
                  )}
                </div>
              </div>

              {/* Features List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(featuresByCategory[category.id] || [])
                  .filter((feature) => {
                    if (!ownerFilter) return true;
                    if (ownerFilter === '__unassigned__') return !feature.owner;
                    return feature.owner === ownerFilter;
                  })
                  .map((feature) => (
                  <div
                    key={feature.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, feature)}
                    onClick={() => handleFeatureClick(feature.id)}
                    style={{
                      backgroundColor: theme.surfaceHover,
                      padding: '12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: `1px solid ${theme.border}`,
                      opacity: draggedFeature?.id === feature.id ? 0.5 : 1,
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = theme.border;
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                      <div style={{ fontWeight: '500', color: theme.text, flex: 1 }}>{feature.name}</div>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {/* Status Badge */}
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '10px',
                            fontWeight: '500',
                            backgroundColor: `${STATUS_COLORS[feature.computed_status || 'not_started']}20`,
                            color: STATUS_COLORS[feature.computed_status || 'not_started'],
                            whiteSpace: 'nowrap',
                          }}
                          title={`Status: ${STATUS_LABELS[feature.computed_status || 'not_started']}`}
                        >
                          {STATUS_LABELS[feature.computed_status || 'not_started']}
                        </span>
                        {/* Priority Badge */}
                        {feature.priority && feature.priority !== 'unscheduled' && (
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: '500',
                              backgroundColor: `${PRIORITY_COLORS[feature.priority]}20`,
                              color: PRIORITY_COLORS[feature.priority],
                              whiteSpace: 'nowrap',
                            }}
                            title={`Priority: ${PRIORITY_LABELS[feature.priority]}`}
                          >
                            {PRIORITY_LABELS[feature.priority]}
                          </span>
                        )}
                        {/* Owner Badge */}
                        {feature.owner && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              backgroundColor: '#3b82f620',
                              borderRadius: '12px',
                              fontSize: '10px',
                              color: '#3b82f6',
                              whiteSpace: 'nowrap',
                            }}
                            title={`Owner: ${feature.owner}`}
                          >
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              backgroundColor: '#3b82f6',
                              color: '#fff',
                              fontSize: '8px',
                              fontWeight: '600',
                            }}>
                              {feature.owner.charAt(0).toUpperCase()}
                            </span>
                            {feature.owner}
                          </span>
                        )}
                      </div>
                    </div>
                    {feature.expectations && (
                      <div style={{ fontSize: '12px', color: theme.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {feature.expectations.substring(0, 80)}...
                      </div>
                    )}
                  </div>
                ))}
                {(featuresByCategory[category.id] || [])
                  .filter((feature) => {
                    if (!ownerFilter) return true;
                    if (ownerFilter === '__unassigned__') return !feature.owner;
                    return feature.owner === ownerFilter;
                  }).length === 0 && (
                  <div style={{ textAlign: 'center', color: theme.textSecondary, fontSize: '14px', padding: '20px' }}>
                    {ownerFilter ? 'No features match filter' : 'Drop features here'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      {selectedFeature && (
        <FeatureDetailModal
          feature={selectedFeature}
          onClose={() => setSelectedFeature(null)}
          onSave={handleSaveFeature}
          onDelete={handleDeleteFeature}
          onMarkDone={handleMarkDone}
          onReopen={handleReopen}
        />
      )}

      {showCreateFeature && (
        <CreateFeatureModal
          categories={categories}
          onClose={() => setShowCreateFeature(false)}
          onCreate={handleCreateFeature}
          initialPriority={priorityFilter}
        />
      )}

      {showCreateCategory && (
        <CreateCategoryModal
          onClose={() => setShowCreateCategory(false)}
          onCreate={handleCreateCategory}
        />
      )}

      {showGeneralPrompt && (
        <GeneralPromptModal
          prompt={generalPrompt}
          onClose={() => setShowGeneralPrompt(false)}
          onSave={handleSaveGeneralPrompt}
        />
      )}

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .features-action-bar {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .features-action-bar > div:first-child {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
          }
          .features-action-bar select {
            max-width: 100% !important;
            width: 100% !important;
          }
          .feature-actions {
            justify-content: stretch !important;
          }
          .feature-actions button {
            flex: 1 !important;
          }
          .categories-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Features;
